import React from 'react';
import { Clover, Sparkles, Zap, ShieldCheck, Gamepad2 } from 'lucide-react';

/**
 * ProductBoxArt – Renders the product's uploaded artwork if provided (image_url),
 * otherwise falls back to a CSS-composed 3D "product box" mockup.
 */
export default function ProductBoxArt({ product, size = 'md', showFeatures = true }) {
  const accent = product.accent || '#4DA3FF';
  const accent2 = product.accent_2 || '#7DD3FC';

  if (product.image_url) {
    return (
      <div
        className="relative aspect-[4/5] w-full rounded-2xl overflow-hidden border border-white/[0.08]"
        style={{
          background:
            `radial-gradient(120% 90% at 50% 10%, ${accent}18 0%, transparent 55%),` +
            `#0b0b0d`,
        }}
        data-testid={`boxart-${product.id}`}
      >
        {/* Ambient glow behind the box */}
        <div
          className="orb"
          style={{
            top: '20%', left: '50%', transform: 'translateX(-50%)',
            width: '85%', height: '70%',
            background: `radial-gradient(circle, ${accent}55 0%, transparent 65%)`,
          }}
        />
        <img
          src={product.image_url}
          alt={product.name}
          loading="eager"
          decoding="async"
          className="relative w-full h-full object-contain object-center select-none p-2"
          draggable={false}
          style={{ imageRendering: 'auto' }}
        />
        {/* Bottom edge glow */}
        <div
          className="absolute -bottom-6 inset-x-6 h-8 rounded-full blur-2xl pointer-events-none"
          style={{ background: accent, opacity: 0.5 }}
        />
      </div>
    );
  }

  return (
    <div
      className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden border border-white/[0.08]"
      style={{
        background:
          `radial-gradient(120% 90% at 50% 10%, ${accent}22 0%, transparent 55%),` +
          `radial-gradient(120% 100% at 50% 100%, ${accent2}18 0%, transparent 60%),` +
          `#0b0b0d`,
      }}
      data-testid={`boxart-${product.id}`}
    >
      <div
        className="orb"
        style={{
          top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: '70%', height: '55%',
          background: `radial-gradient(circle, ${accent}66 0%, transparent 70%)`,
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />
      <div className="absolute inset-x-0 top-0 p-5 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Clover className="h-4 w-4 text-white/90" style={{ filter: `drop-shadow(0 0 8px ${accent})` }} />
          <div>
            <div className="font-display font-black text-white text-xl leading-none tracking-tight"
                 style={{ textShadow: `0 0 18px ${accent}88` }}>
              LUCKY
            </div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-white/60 mt-0.5">{product.tagline}</div>
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-[0.25em] px-2 py-1 rounded-full border border-white/15 bg-white/5 text-white/70 font-mono">
          {product.coming_soon ? 'Soon' : 'Live'}
        </span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[70%] h-[45%]">
          <div className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: `${accent}55`, boxShadow: `0 0 40px ${accent}55, inset 0 0 40px ${accent2}33` }} />
          <div className="absolute inset-4 rounded-full border" style={{ borderColor: `${accent2}66` }} />
          <div className="absolute inset-10 rounded-full"
            style={{ background: `radial-gradient(circle, ${accent2}88 0%, transparent 65%)`, filter: 'blur(6px)' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Gamepad2 className="h-10 w-10 text-white/90 drop-shadow-[0_0_16px_rgba(255,255,255,0.7)]" />
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4 space-y-2">
        {showFeatures && (
          <div className="space-y-1">
            <FeatureRow icon={<Gamepad2 className="h-3 w-3" />} label={product.tag} accent={accent} />
            <FeatureRow icon={<Zap className="h-3 w-3" />} label="Optimised for XIM & Cronus" accent={accent} />
            <FeatureRow icon={<Sparkles className="h-3 w-3" />} label="Instant Digital Delivery" accent={accent} />
            <FeatureRow icon={<ShieldCheck className="h-3 w-3" />} label="Secure Auth Downloads" accent={accent} />
          </div>
        )}
        <div className="pt-2 mt-2 border-t border-white/10 flex items-baseline justify-between">
          <span className="font-display font-black text-white text-xl tracking-tight">{product.name}</span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-mono">
            {product.device === 'zen' ? 'Zen Edition' : 'Matrix Edition'}
          </span>
        </div>
      </div>
      <div className="absolute -bottom-6 inset-x-6 h-8 rounded-full blur-2xl"
        style={{ background: accent, opacity: 0.4 }} />
    </div>
  );
}

function FeatureRow({ icon, label, accent }) {
  return (
    <div className="flex items-center gap-2 text-[10px] text-white/80 font-medium">
      <span className="inline-flex items-center justify-center h-4 w-4 rounded-full"
        style={{ background: `${accent}33`, color: accent }}>
        {icon}
      </span>
      <span className="uppercase tracking-wider">{label}</span>
    </div>
  );
}
